import { AlertCircle, Delete, Trash2 } from 'lucide-react'
import clsx from 'clsx'

import { plataCentavos } from '@/lib/formato'
import { normalizarTipeado, textoDeCentavos } from './montoTipeado'
import type { MedioPagoPos } from './tipos'
import type { PagoCargado } from './cobroSinDetalle'
import { useTeclaAgregar } from './useTeclaAgregar'

// Las piezas del cobro sin detalle.
//
// Las comparten el diálogo del F3 y la pantalla del local que solo cobra: la
// LÓGICA vive en `useCobroSinDetalle` y esto es lo que se ve. Están acá y no
// duplicadas porque el cajero es el mismo y tiene que reconocer el teclado y
// los botones de medio sean seis de la mañana en el diálogo o todo el día en la
// pantalla; dos versiones que se van separando es una pantalla que se aprende
// dos veces.
//
// `grande` es lo único que cambia entre las dos: la pantalla completa tiene
// espacio y el mostrador se opera con el dedo, así que ahí las teclas van más
// altas y el importe más grande. No es estética: es el tamaño con el que se
// acierta sin mirar.

interface Tecla {
  digito: (d: string) => void
  coma: () => void
  borrar: () => void
  limpiar: () => void
}

/**
 * El importe que se está tipeando, grande y en el centro.
 *
 * La ETIQUETA la manda el hook y no está fija, y eso es lo que arregla la
 * pantalla que no se entendía: el mismo teclado carga el total del ticket o el
 * importe de un pago de la división, y antes decía «Monto del ticket» en los
 * dos casos —así que tipear parecía cambiar una cosa y cambiaba la otra—.
 */
export function MontoTipeado({
  texto,
  etiqueta,
  contexto,
  refInput,
  grande = false,
}: {
  texto: string
  etiqueta: string

  /** Un renglón abajo con el total y lo que falta, dividiendo. */
  contexto?: React.ReactNode

  refInput?: React.Ref<HTMLInputElement>
  grande?: boolean
}) {
  return (
    <div
      className={clsx(
        'rounded-xl border-2 border-verde/30 bg-crema-alt text-center',
        grande ? 'p-5' : 'p-4',
      )}
    >
      <p className="text-[12.5px] font-medium uppercase tracking-wide text-tinta-tenue">
        {etiqueta}
      </p>

      {/* Un input `readOnly` y no un `<p>`: se carga con el teclado de acá o
          con el de la máquina, y así el foco arranca en el monto —que es donde
          el cajero espera estar— sin que la tablet abra el teclado del sistema
          encima de la pantalla. */}
      <input
        ref={refInput}
        inputMode="numeric"
        value={texto}
        readOnly
        aria-label="Monto del ticket"
        className={clsx(
          'num mt-1 w-full bg-transparent text-center font-bold leading-none text-tinta outline-none',
          grande ? 'text-[56px]' : 'text-[40px]',
        )}
      />

      {contexto && <div className="mt-2 text-[13px] text-tinta-suave">{contexto}</div>}
    </div>
  )
}

/**
 * El teclado de calculadora, con su coma.
 *
 * `estirar` lo deja ocupar el alto que sobra, que es lo que corresponde en la
 * pantalla completa: en el mostrador se toca con el dedo y una tecla más grande
 * es una tecla que se acierta sin mirar. Nunca baja del mínimo, así que en una
 * pantalla corta las teclas quedan como en el diálogo en lugar de aplastarse.
 */
export function TecladoMonto({
  tecla,
  grande = false,
  estirar = false,
}: {
  tecla: Tecla
  grande?: boolean
  estirar?: boolean
}) {
  return (
    <div
      className={clsx(
        'grid grid-cols-3',
        grande ? 'gap-2.5' : 'gap-2',
        // Solo en escritorio: en un teléfono las dos columnas se apilan y la
        // página scrollea, así que estirar acá aplastaría las teclas.
        estirar && 'md:min-h-0 md:flex-1 md:auto-rows-fr',
      )}
    >
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
        <BotonTecla key={d} onClick={() => tecla.digito(d)} grande={grande} estirar={estirar}>
          {d}
        </BotonTecla>
      ))}

      {/* La coma está en el teclado y no solo en el del hardware: la tablet del
          mostrador no tiene numpad, y el importe con centavos es justamente el
          que llega de la balanza. */}
      <BotonTecla onClick={tecla.coma} aria-label="Coma decimal" grande={grande} estirar={estirar}>
        ,
      </BotonTecla>

      <BotonTecla onClick={() => tecla.digito('0')} grande={grande} estirar={estirar}>
        0
      </BotonTecla>

      <BotonTecla onClick={tecla.borrar} aria-label="Borrar" grande={grande} estirar={estirar}>
        <Delete className={clsx('mx-auto', grande ? 'h-6 w-6' : 'h-5 w-5')} />
      </BotonTecla>

      <BotonTecla
        onClick={tecla.limpiar}
        aria-label="Borrar todo"
        clase={clsx('col-span-3', grande ? 'h-12 text-[16px]' : 'h-10 text-[15px]')}
      >
        C
      </BotonTecla>
    </div>
  )
}

function BotonTecla({
  onClick,
  children,
  clase,
  grande = false,
  estirar = false,
  ...resto
}: {
  onClick: () => void
  children: React.ReactNode
  clase?: string
  grande?: boolean
  estirar?: boolean
} & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      // El `mousedown` no roba el foco: sin esto, tocar una tecla con el mouse
      // deja el foco en el BOTÓN, y desde ahí un medio de pago con tecla rápida
      // de un solo dígito se pelearía con lo que se tipea. Con el foco quieto
      // en el campo del importe, el dígito es siempre del importe y la tecla
      // del medio sigue andando con las de función.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      {...resto}
      className={clsx(
        'num rounded-xl border border-borde bg-papel font-semibold text-tinta transition hover:border-verde active:scale-95',
        estirar
          ? 'h-16 text-[22px] md:h-full md:min-h-[52px]'
          : grande
            ? 'h-16 text-[22px]'
            : 'h-12 text-[19px]',
        clase,
      )}
    >
      {children}
    </button>
  )
}

/** Los medios de pago con su tecla rápida. */
export function BotonesMedio({
  cobrables,
  medio,
  onElegir,
  grande = false,
}: {
  cobrables: MedioPagoPos[]
  medio: MedioPagoPos | null
  onElegir: (m: MedioPagoPos) => void
  grande?: boolean
}) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {cobrables.map((m) => (
        <button
          key={m.id}
          type="button"
          onClick={() => onElegir(m)}
          className={clsx(
            'flex flex-col items-start justify-center gap-0.5 rounded-xl border px-3 py-2 text-left transition',
            grande ? 'min-h-[60px]' : 'min-h-[48px]',
            medio?.id === m.id
              ? 'border-verde bg-verde-tenue'
              : 'border-borde bg-papel hover:border-borde-fuerte',
          )}
        >
          <span
            className={clsx(
              'font-semibold text-tinta',
              grande ? 'text-[15px]' : 'text-[13.5px]',
            )}
          >
            {m.nombre}
          </span>

          {m.teclaRapida && (
            <span className="num text-[11px] text-tinta-tenue">{m.teclaRapida}</span>
          )}
        </button>
      ))}
    </div>
  )
}

/** El número que pide el medio de pago (cupón, lote, comprobante del QR). */
export function CampoReferencia({
  id,
  valor,
  onCambio,
}: {
  id: string
  valor: string
  onCambio: (v: string) => void
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[13px] font-medium text-tinta-suave">
        Número de comprobante
        <span className="ml-1 font-normal text-tinta-tenue">(obligatorio)</span>
      </label>

      <input
        id={id}
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        placeholder="Cupón, lote o comprobante del QR"
        autoComplete="off"
        className="w-full rounded-lg border border-borde bg-papel px-3 py-2.5 text-[14px]
                   outline-none focus:border-verde"
      />
    </div>
  )
}

/**
 * Por qué no se puede cobrar todavía.
 *
 * El botón se deshabilitaba en silencio cuando el medio elegido pide referencia
 * y el campo está vacío, y desde afuera eso se ve como un sistema que no anda:
 * el monto está puesto, el medio está elegido y el botón no responde. Que el
 * número sea obligatorio lo decide el LOCAL, medio por medio, así que el cartel
 * también dice dónde se cambia.
 */
export function AvisoReferencia({ medio }: { medio: MedioPagoPos | null }) {
  return (
    <p className="flex items-start gap-1.5 text-left text-[12.5px] leading-relaxed text-ambar">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span className="text-left">
        {medio?.nombre} pide el número de comprobante. Si en tu local no hace falta, se saca en
        Configuración → Medios de pago.
      </span>
    </p>
  )
}

/** El número del papel que emitió el otro equipo. Opcional y configurable. */
export function CampoComprobante({
  id,
  valor,
  onCambio,
}: {
  id: string
  valor: string
  onCambio: (v: string) => void
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-[13px] font-medium text-tinta-suave">
        Comprobante del otro equipo
      </label>

      <input
        id={id}
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        placeholder="Opcional: el número que salió impreso"
        autoComplete="off"
        className="w-full rounded-lg border border-borde bg-papel px-3 py-2.5 text-[14px]
                   outline-none focus:border-verde"
      />

      <p className="mt-1 text-[12px] text-tinta-tenue">
        Es con lo que después se cruza este registro contra el papel.
      </p>
    </div>
  )
}

/**
 * El cobro dividido entre dos o más medios.
 *
 * Es la parte que el mostrador reportó como incomprensible, y la comparten las
 * dos pantallas para que se entienda igual en las dos. Lo que la hace legible:
 *
 *  - Se ve SIEMPRE cuánto falta, que es la cuenta que el cajero no tiene que
 *    hacer de cabeza.
 *  - El botón dice el importe exacto que va a agregar, y con el campo vacío
 *    dice «lo que falta»: así el último pago es un solo toque.
 *  - Cuando NO se puede agregar, dice por qué. Un botón apagado sin motivo se
 *    lee como un sistema que no anda.
 *  - Y hay salida: «Volver a un solo medio» limpia los pagos. Sin eso, un
 *    error de tipeo obliga a cerrar la pantalla y empezar de nuevo.
 */
export function CobroDividido({
  pagos,
  onQuitar,
  onAgregar,
  onVolver,
  puedeAgregar,
  motivoNoAgregar,
  importeAAgregar,
  pagoCentavos,
  falta,
  medio,
}: {
  pagos: PagoCargado[]
  onQuitar: (indice: number) => void
  onAgregar: () => void
  onVolver: () => void
  puedeAgregar: boolean
  motivoNoAgregar: string | null
  importeAAgregar: number
  pagoCentavos: number
  falta: number
  medio: MedioPagoPos | null
}) {
  // «+» agrega el pago, igual que en el diálogo de cobro del POS: las dos
  // pantallas que cobran no pueden tener dos formas de hacer lo mismo.
  useTeclaAgregar(onAgregar, { activo: puedeAgregar && falta > 0 })

  return (
    <div className="space-y-2.5 rounded-xl border border-borde bg-crema-alt p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-medium uppercase tracking-wide text-tinta-tenue">
          Cobro dividido
        </span>

        <button
          type="button"
          onClick={onVolver}
          className="text-[12.5px] text-tinta-tenue underline decoration-borde-fuerte underline-offset-2 hover:text-verde-oscuro"
        >
          Volver a un solo medio
        </button>
      </div>

      {pagos.length > 0 && <PagosCargados pagos={pagos} onQuitar={onQuitar} />}

      {falta > 0 ? (
        <>
          <button
            type="button"
            onClick={onAgregar}
            disabled={!puedeAgregar}
            className="btn-secundario w-full"
          >
            {!puedeAgregar
              ? 'Agregar el pago'
              : pagoCentavos > 0
                ? `Agregar ${plataCentavos(importeAAgregar)} con ${medio?.nombre}`
                : `Agregar lo que falta (${plataCentavos(falta)}) con ${medio?.nombre}`}{' '}
            <span className="num opacity-70">+</span>
          </button>

          {motivoNoAgregar ? (
            <p className="flex items-start gap-1.5 text-left text-[12.5px] leading-relaxed text-ambar">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="text-left">{motivoNoAgregar}</span>
            </p>
          ) : (
            <p className="text-[12px] leading-relaxed text-tinta-tenue">
              Poné el importe de este pago con el teclado, o dejalo en cero para agregar todo lo
              que falta.
            </p>
          )}

          <p className="flex items-baseline justify-between gap-2 border-t border-borde pt-2 text-[13px]">
            <span className="uppercase tracking-wide text-rojo">Falta</span>
            <span className="num font-bold text-rojo">{plataCentavos(falta)}</span>
          </p>
        </>
      ) : (
        <p className="flex items-baseline justify-between gap-2 border-t border-borde pt-2 text-[13px]">
          <span className="uppercase tracking-wide text-verde-oscuro">Cubierto</span>
          <span className="num font-bold text-verde-oscuro">
            {plataCentavos(pagos.reduce((a, p) => a + p.importeCentavos, 0))}
          </span>
        </p>
      )}
    </div>
  )
}

/** Los pagos cargados a mano, cuando el cobro se divide en más de un medio. */
export function PagosCargados({
  pagos,
  onQuitar,
}: {
  pagos: PagoCargado[]
  onQuitar: (indice: number) => void
}) {
  return (
    <ul className="space-y-1.5">
      {pagos.map((p, i) => (
        <li key={i} className="flex items-center justify-between gap-2 text-[13.5px]">
          <span className="min-w-0 flex-1 truncate text-tinta-suave">
            {p.medio.nombre}
            {p.referencia && <span className="text-tinta-tenue"> ({p.referencia})</span>}
          </span>

          <span className="num font-medium text-tinta">{plataCentavos(p.importeCentavos)}</span>

          <button
            type="button"
            onClick={() => onQuitar(i)}
            aria-label={`Quitar el pago con ${p.medio.nombre}`}
            className="grid h-7 w-7 place-items-center rounded text-tinta-tenue hover:bg-rojo/10 hover:text-rojo"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * Con cuánto paga y el vuelto.
 *
 * El cliente pagó en el mostrador aunque el ticket lo haya emitido otro equipo,
 * así que el vuelto se sigue cantando desde acá.
 */
export function PanelEntregado({
  id,
  entregadoTexto,
  montoCentavos,
  entregado,
  onCambio,
  vuelto,
  grande = false,
}: {
  id: string
  entregadoTexto: string | null
  montoCentavos: number
  entregado: number
  onCambio: (v: string) => void
  vuelto: number | null
  grande?: boolean
}) {
  return (
    <div className="rounded-xl border border-borde bg-papel p-4">
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-tinta-suave">
        ¿Con cuánto paga?
      </label>

      <input
        id={id}
        inputMode="numeric"
        // Muestra el monto mientras nadie lo toque: pagar justo es el caso más
        // común y arrancar en blanco obliga a tipear el mismo número dos veces.
        value={entregadoTexto ?? textoDeCentavos(montoCentavos)}
        // Los mismos centavos que el monto: el vuelto de un ticket de $1.487,50
        // pagado con $1.500 son $12,50, y con el campo en enteros no había forma
        // de escribirlo.
        onChange={(e) => onCambio(normalizarTipeado(e.target.value))}
        placeholder="Opcional"
        className="num w-full rounded-lg border border-borde bg-crema-alt px-3 py-2.5
                   text-right text-[18px] outline-none focus:border-verde"
      />

      {vuelto !== null && (
        <div className="mt-3 rounded-lg bg-verde-tenue px-3 py-2.5">
          <p className="text-[12.5px] font-medium uppercase tracking-wide text-verde-oscuro">
            Vuelto
          </p>

          <p
            className={clsx(
              'num font-bold text-verde-oscuro',
              grande ? 'text-[36px]' : 'text-[28px]',
            )}
          >
            {plataCentavos(vuelto)}
          </p>
        </div>
      )}

      {entregado < montoCentavos && (
        <p className="mt-2 flex items-start gap-1.5 text-[12.5px] text-ambar">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Con eso no alcanza para cubrir el monto.
        </p>
      )}
    </div>
  )
}
